import type { FormEvent } from 'react';
import React from 'react';

import type { Leaves } from '@aline/common';
import { DialogActions, DialogContent, InputLabel } from '@mui/material';
import { Formik } from 'formik';
import { AlineButton, AlineDialog, FormikAlineInput } from 'src/components';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

const useStyles = makeStyles()(theme => ({
  container: {
    width: 348,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    margin: theme.spacing(1, 0),
  },
  saveButton: {
    marginLeft: theme.spacing(1),
  },
}));

interface InsertTableDialogProps {
  handleClose: () => void;
  insertTable: (rowCount: number, columnCount: number) => void;
  open: boolean;
}

interface InsertTableForm {
  columnCount: number;
  rowCount: number;
}

const NewTableSchema = Yup.object<InsertTableForm>().shape({
  columnCount: Yup.number()
    .typeError('Column count must be a number')
    .positive('Column count must be a positive number')
    .required('Required'),
  rowCount: Yup.number()
    .typeError('Row count must be a number')
    .positive('Row count must be a positive number')
    .required('Required'),
});
const rowCountKey: Leaves<InsertTableForm> = 'rowCount';
const colCountKey: Leaves<InsertTableForm> = 'columnCount';

export function InsertTableDialog({
  handleClose,
  insertTable,
  open,
}: InsertTableDialogProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.container }}
      open={open}
      title="Insert table"
      onClose={handleClose}
    >
      <Formik<InsertTableForm>
        initialValues={{ columnCount: 1, rowCount: 1 }}
        validationSchema={NewTableSchema}
        onSubmit={({ columnCount, rowCount }) => {
          insertTable(rowCount, columnCount);
          handleClose();
        }}
      >
        {({ handleReset, isSubmitting, isValid, setFieldValue, submitForm }): JSX.Element => (
          <form
            onReset={handleReset}
            onSubmit={(event: FormEvent<HTMLFormElement>): void => {
              event.preventDefault();
              event.stopPropagation();
              submitForm();
            }}
          >
            <DialogContent className={classes.content}>
              <InputLabel className={classes.inputLabel} htmlFor="rowCount">
                Number of rows
              </InputLabel>
              <FormikAlineInput
                autoFocus
                fieldName={rowCountKey}
                stepDown={val => {
                  const numVal = typeof val === 'number' ? val : 0;
                  setFieldValue(rowCountKey, Math.max(1, numVal - 1));
                }}
                stepUp={val => {
                  const numVal = typeof val === 'number' ? val : 0;
                  setFieldValue(rowCountKey, numVal + 1);
                }}
                type="number"
              />
              <InputLabel className={classes.inputLabel} htmlFor="columnCount">
                Number of columns
              </InputLabel>
              <FormikAlineInput
                fieldName={colCountKey}
                stepDown={val => {
                  const numVal = typeof val === 'number' ? val : 0;
                  setFieldValue(colCountKey, Math.max(1, numVal - 1));
                }}
                stepUp={val => {
                  const numVal = typeof val === 'number' ? val : 0;
                  setFieldValue(colCountKey, numVal + 1);
                }}
                type="number"
              />
            </DialogContent>
            <DialogActions>
              <AlineButton color="primary" variant="outlined" onClick={handleClose}>
                Cancel
              </AlineButton>
              <AlineButton
                className={classes.saveButton}
                color="primary"
                disabled={!isValid || isSubmitting}
                loading={isSubmitting}
                type="submit"
                variant="contained"
              >
                Insert
              </AlineButton>
            </DialogActions>
          </form>
        )}
      </Formik>
    </AlineDialog>
  );
}
